import { useCallback, useEffect, useMemo } from 'react';

import { useWorkspacesPageSearchSuppliersLazyQuery } from '../../../generated';

export interface SupplierOption {
  id: string;
  name: string;
  /** The distributor's short reference, e.g. 00HHA. */
  referenceId: string;
  /** Unset on legacy records, which the supplier service reads as Warehouse. */
  hierarchyType: string | null;
}

export interface UseSupplierSearchResult {
  suppliers: SupplierOption[];
  isSearching: boolean;
  searchSuppliers: (searchTerm: string) => void;
}

/** Keyword search only answers usefully from the third character. */
export const SUPPLIER_SEARCH_MIN_CHARS = 3;

const RESULT_LIMIT = 30;

/**
 * Suppliers to root a workspace on.
 *
 * A first page loads with no keywords, so there is something to pick before
 * anybody types. One and two character terms keep that page on screen: the
 * search does not narrow usefully on them, and blanking the list would read as
 * "no suppliers".
 */
export const useSupplierSearch = (): UseSupplierSearchResult => {
  const [executeSearch, { data, loading }] =
    useWorkspacesPageSearchSuppliersLazyQuery({
      fetchPolicy: 'cache-and-network',
    });

  const runSearch = useCallback(
    (keywords?: string) => {
      executeSearch({ variables: { keywords, limit: RESULT_LIMIT } });
    },
    [executeSearch]
  );

  useEffect(() => {
    runSearch();
  }, [runSearch]);

  const searchSuppliers = useCallback(
    (searchTerm: string) => {
      const keywords = searchTerm.trim();

      if (keywords.length === 0) {
        runSearch();
        return;
      }

      if (keywords.length < SUPPLIER_SEARCH_MIN_CHARS) {
        return;
      }

      runSearch(keywords);
    },
    [runSearch]
  );

  const suppliers = useMemo(() => {
    const search = data?.supplierSearch;

    // Narrowed on `edges` rather than `__typename`: codegen types the latter as
    // optional on every member of the union, so it discriminates neither arm.
    if (!search || !('edges' in search)) {
      return [];
    }

    return search.edges.map((edge) => ({
      id: edge.node.id,
      name: edge.node.name,
      referenceId: edge.node.referenceId,
      hierarchyType: edge.node.hierarchyType ?? null,
    }));
  }, [data]);

  return { suppliers, isSearching: loading, searchSuppliers };
};
